import { Box, ButtonProps, Stack, StackProps, SxProps, Theme, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { cloneElement, forwardRef, ReactElement, ReactNode } from 'react';

export interface EmptyStateOwnProps {
  /**
   * Title sentence. Should not end with a full stop.
   * @example "No matching items found"
   */
  title: ReactNode;
  /**
   * Subtitle ending with a full stop.
   * @example "Try adjusting your filters or search terms."
   */
  subtitle?: ReactNode;
  /**
   * Button element (size="small" will be applied automatically).
   * @example `<Button>Clear filters</Button>`
   */
  action?: ReactElement<{ size: ButtonProps['size'] }>;
  /**
   * Empty state icon or illustration
   */
  indicator?: ReactNode;
  /**
   * Additional content to display below the empty state message. Buttons are expected to
   * have size="small".
   */
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

export type EmptyStateProps = EmptyStateOwnProps & Omit<StackProps, 'sx'>;

const Container = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  flex: 1,
  padding: theme.spacing(6),
}));

/**
 * An empty state message with a title, subtitle, and optional actions.
 */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { title, subtitle, action, indicator, children, ...rest },
  ref,
) {
  return (
    <Container {...rest} ref={ref}>
      {indicator}
      <Typography
        variant="label1"
        sx={{
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{
          textAlign: 'center',
        }}
      >
        {subtitle}
      </Typography>
      {action && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          {cloneElement(action, { size: 'small' })}
        </Box>
      )}
      {children}
    </Container>
  );
});
